import { ComponentType, createElement, KeyboardEvent, memo, MouseEvent, ReactNode, useCallback, useState } from "react";
import classNames from "classnames";
import BaseModal from "react-modal";
import { Button, ButtonProps, Link, LinkProps } from "../../ui";

import styles from "./style.module.scss";

export type ConfirmationComponentProps = {
  title?: string,
  onSubmit?: (result: boolean) => boolean | Promise<boolean>,
  submitText?: string,
  rejectText?: string,
  children?: ReactNode,
  highlightPosition?: "left" | "right",
  submitPosition?: "left" | "right",
  position?: "topRight" | "center",
  overlayClassName?: string,
  className?: string,
} & Omit<BaseModal.Props, "overlayClassName" | "className">

const buttonProps: ButtonProps = {
  buttonType: "secondary",
  buttonColorSchema: "outlined",
  autoFocus: true,
};

const linkProps: ButtonProps<LinkProps> = {
  linkType: "m",
  component: "button",
};

export const Confirmation = memo<ConfirmationComponentProps>((
  {
    className,
    title,
    onRequestClose,
    submitText,
    rejectText,
    onSubmit,
    overlayClassName,
    children,
    highlightPosition,
    submitPosition,
    position,
    ...rest
  }) => {

  const [ isSubmitting, setIsSubmitting ] = useState(false);

  const onSubmitClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    try {
      setIsSubmitting(true);
      if (await onSubmit?.(true)) {
        onRequestClose?.(e);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [ onSubmit, onRequestClose, setIsSubmitting ]);

  const onRejectClick = useCallback(async (e: MouseEvent | KeyboardEvent) => {
    try {
      setIsSubmitting(true);
      if (await onSubmit?.(false)) {
        onRequestClose?.(e);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [ onSubmit, onRequestClose, setIsSubmitting ]);

  const submitProps = {
    onClick: onSubmitClick,
    children: submitText,
    type: "submit",
  };

  const rejectProps = {
    onClick: onRejectClick,
    children: rejectText,
  };

  return (
    <BaseModal
      {...rest}
      overlayClassName={classNames(styles.overlay, overlayClassName, {
        [styles.overlayTopRight]: position === "topRight",
        [styles.overlayCenter]: position === "center",
      })}
      className={classNames(className, styles.root)}
      onRequestClose={onRequestClose}
    >
      {
        title &&
        <div className={styles.header}>
          {title}
        </div>
      }
      <div className={styles.content}>
        {children}
      </div>
      <div className={styles.buttons}>
        {
          createElement(
            highlightPosition === "left" ? Button : Link,
            {
              ...(highlightPosition === "left" ? buttonProps : linkProps),
              ...(submitPosition === "left" ? submitProps : rejectProps),
              disabled: isSubmitting,
            },
          )
        }
        {
          createElement(
            highlightPosition === "right" ? Button : Link,
            {
              ...(highlightPosition === "right" ? buttonProps : linkProps),
              ...(submitPosition === "right" ? submitProps : rejectProps),
              disabled: isSubmitting,
            },
          )
        }
      </div>
    </BaseModal>
  );
});


(Confirmation as ComponentType<ConfirmationComponentProps>).defaultProps = {
  rejectText: "No",
  submitText: "Yes",
  shouldCloseOnEsc: false,
  shouldCloseOnOverlayClick: false,
  onSubmit: () => true,
  highlightPosition: "right",
  submitPosition: "left",
  position: "topRight",
};

export default Confirmation;
